<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { prefixCDN } from '@/utils/cdn'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

// 定义样式变量供 CSS v-bind 使用
const avatarBorderColor = props.config.theme.avatarBorder
const memberBgUrl = computed(() => `url(${props.config.resources.memberBg}) no-repeat top center`)
</script>

<template>
  <div class="page-cover">
    <!-- 头像 -->
    <div class="px-30px pt-40px pb-27px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-15 h-15 border rounded-full avatar-border" :src="prefixCDN(userStore.$state.thirdHeadImgUrl)" alt="avatar">
        <img v-else class="w-15 h-15 rounded-full" :src="prefixCDN(config.resources.userDefaultAvatar)" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="username overflow-hidden whitespace-nowrap text-ellipsis">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
          <div v-if="userStore.$state.subscribe" class="shrink-0">
            <img src="../icons/vowpath/vip.png" class="w-18px h-14px ml-8px">
          </div>
        </div>
        <p class="uid">
          UID {{ userStore.$state.code }}
        </p>
      </div>

      <div v-if="!userStore.$state.thirdPartType" type="link" class="shrink-0">
        <a class="px-14px py-7px text-n-text-1 text-13px font-bold rounded-full border border-n-text-inverse" @click="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>

    <!-- vip 订阅 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover flex items-center justify-between mx-10px px-16px">
      <div class="flex-1 flex items-center">
        <div>
          <div class="vip-title">
            <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
            <span v-else>{{ config.texts.vipMembership }}</span>
          </div>
          <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
            {{ config.texts.moreCoinsMoreBenefits }}
          </div>
          <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
            {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
          </div>
        </div>
      </div>
      <router-link
        v-if="!userStore.$state.subscribe"
        :to="{ name: 'subscribe' }"
      >
        <div class="details-btn flex items-center justify-center" @click="elementClick('Details')">
          {{ config.texts.details }}
        </div>
      </router-link>
    </div>

    <!-- 金币余额卡片 -->
    <!-- v-if="userStore.$state.thirdPartType"  -->
    <div class="coin-card mx-10px px-16px flex items-center justify-between">
      <div class="flex items-center">
        <img src="/images/vowpath/coins/coin-diamond.webp" class="coin-diamond shrink-0" alt="">
        <span class="coin-balance-text">{{ userStore.coinBalance }}</span>
      </div>
      <router-link :to="{ name: 'pay' }">
        <div class="top-up-btn flex items-center justify-center" @click="elementClick('TopUp')">
          Top Up
        </div>
      </router-link>
    </div>

    <!-- setting -->
    <div class="mt-10px mx-10px px-20px b-rd-2 bg-n-bg-card">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-transaction.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-unlock.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-contact.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-setting.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.page-cover {
  width: 100%;
  min-height: 100vh;
  background: v-bind(memberBgUrl);
  background-size: 100% 100%;
}

.avatar-border {
  border-color: v-bind(avatarBorderColor);
}

.vip-cover {
  width: 355px;
  height: 90px;
  background: url('../images/vowpath/member/vip-bg.png') no-repeat center;
  background-size: cover;
  border-radius: 8px;
  box-sizing: border-box;
}

.vip-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 14px;
  color: white;
}

.vip-subtitle {
  font-weight: 400;
  font-size: 12px;
  color: #EFDCFF;
  margin-top: 6px;
}

.details-btn {
  width: 80px;
  height: 32px;
  background: linear-gradient(270deg, #FF6BD2 0%, #A162FF 100%);
  border-radius: 20px;
  font-weight: 500;
  font-size: 14px;
  color: #fff;
}

.coin-card {
  height: 60px;
  background: rgba(255, 255, 255, 1);
  border-radius: 8px;
  box-shadow: 0px 2px 10px 0px rgba(220, 224, 228, 0.4);
  padding: 14px 16px;
  box-sizing: border-box;
  position: relative;
  margin-top: -10px;
}

.coin-diamond {
  width: 31px;
  height: 27px;
  margin-right: 8px;
}

.coin-balance-text {
  font-size: 16px;
  font-family: HelveticaNeue-Medium, sans-serif;
  font-weight: 500;
  color: rgba(50, 53, 58, 1);
}

.top-up-btn {
  height: 32px;
  border-radius: 18px;
  padding: 0 15px;
  background: linear-gradient(225deg, rgba(248, 107, 205, 1) 0%, rgba(156, 94, 249, 1) 100%);
  font-size: 14px;
  font-family: Inter-Medium, sans-serif;
  font-weight: 500;
  color: #fff;
}

.username {
  height: 18px;
  font-weight: 500;
  font-size: 18px;
  color: #32353A;
  line-height: 18px;
}

.uid {
  font-weight: 400;
  font-size: 12px;
  color: #969CAA;
  line-height: 11px;
  margin-top: 8px;
}
</style>
